import { useEffect } from 'react';
import { useIsClient } from 'usehooks-ts';

import { logout } from '@store/actions/action-auth';
import { useGetAuthSessionQuery } from '@store/auth.api';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import raiseToast from '@components/toast';

/**
 * Hook that validates the user's session and logs them out if invalid.
 * Uses RTK Query to check session validity with focus/reconnect refetching.
 *
 * This is a fallback mechanism for when the user returns to the page and their
 * cookie has expired.
 * If a 401 is received from Platform for an authenticated request or the Redis
 * session entry expires (same expiry as the cookie) a socket message is sent to
 * the client to log them out.
 */
export const useSessionValidator = () => {
  const dispatch = useAppDispatch();
  // This hook is used in client-init so we need to ensure it only runs on the client
  const isClient = useIsClient();
  const { isAuthenticated } = useAppSelector(state => state.auth);

  const { error } = useGetAuthSessionQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: false,
    skip: !isClient || !isAuthenticated,
  });

  useEffect(() => {
    if (
      isClient &&
      isAuthenticated &&
      error &&
      'status' in error &&
      error.status === 401
    ) {
      dispatch(logout());

      raiseToast({
        toastId: 'session_expired_fallback',
        level: 'warning',
        title: 'Session Expired',
        message: 'Your session has expired. Please log in again.',
        autoClose: 8000,
        dismissAll: true,
        icon: 'sign out',
      });
    }
  }, [error, dispatch, isAuthenticated, isClient]);
};
